import type { ReactNode } from 'react'
import { VisuallyHidden } from '@n26/nxd'
import type { Figure } from '@/features/dashboard/format/figure'
import { valueOptions } from '@/features/dashboard/format/figure'
import {
  formatValue,
  formatValueParts,
  trendFromChange,
} from '@/features/dashboard/format/format'
import { DeltaBadge } from './DeltaBadge'
import { Odometer, zeroFrom } from '../charts/Odometer'
import styles from './CardFigure.module.css'

type CardFigureProps = {
  figure: Figure
  /** tile: the 32px figure on a tile; expanded: the 64px one in the panel. */
  size: 'tile' | 'expanded'
  className?: string | undefined
  /** Shown under the change, e.g. the expanded card's deep-dive button. */
  children?: ReactNode
}

export const CardFigure = ({
  figure,
  size,
  className,
  children,
}: CardFigureProps) => {
  const options = valueOptions(figure)
  const { prefix, main, suffix, unit } = formatValueParts(figure.value, options)
  // Like bristol, the figure's digits roll from the previous period's value
  // (from zeros without one), up if it rose and down if it fell.
  const from =
    figure.previous === null || figure.previous === 0
      ? zeroFrom(main)
      : formatValueParts(figure.previous, options).main
  const direction =
    figure.previous !== null && figure.value < figure.previous ? 'down' : 'up'
  return (
    <div className={`${styles.values} ${styles[size]} ${className ?? ''}`}>
      <span className={styles.figure}>
        <VisuallyHidden>{formatValue(figure.value, options)}</VisuallyHidden>
        <span className={styles.figureParts} aria-hidden='true'>
          {prefix}
          <Odometer value={main} from={from} direction={direction} />
          {suffix && (
            <Odometer
              className={styles.suffix}
              value={suffix}
              from={suffix}
              direction={direction}
            />
          )}
          {unit && <span className={styles.suffix}>{unit}</span>}
        </span>
      </span>
      {figure.fraction !== null && (
        <DeltaBadge
          fraction={figure.fraction}
          direction={trendFromChange(figure.fraction)}
          size={size === 'tile' ? 'small' : 'large'}
        />
      )}
      {children}
    </div>
  )
}
